import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { humanizeApiError } from "../api/client";
import { type User, type UserPatchPayload, usersApi } from "../api/users";
import { Breadcrumbs } from "../components/ui/breadcrumbs";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";

type ConfirmKind = "revoke" | "deactivate";

export function UserDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setUser(await usersApi.get(id));
    } catch (err) {
      setError(extractMessage(err));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function action(name: string, fn: () => Promise<User>) {
    setBusy(name);
    setError(null);
    try {
      const updated = await fn();
      setUser(updated);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (!user) {
    return (
      <div className="space-y-2">
        <BackToUsers />
        <Breadcrumbs
          items={[
            { label: "Сотрудники", to: "/users" },
            { label: "Сотрудник" },
          ]}
        />
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Загрузка…</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <BackToUsers />
      <Breadcrumbs
        items={[
          { label: "Сотрудники", to: "/users" },
          { label: user.full_name },
        ]}
      />

      <ProfileCard
        user={user}
        busy={busy === "patch"}
        onSave={(payload) =>
          action("patch", () => usersApi.patch(user.id, payload))
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Действия</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="destructive"
              disabled={busy !== null}
              onClick={() => setConfirmKind("revoke")}
            >
              {busy === "revoke" ? "…" : "Отозвать все сессии"}
            </Button>
            {user.is_active ? (
              <Button
                variant="destructive"
                disabled={busy !== null}
                onClick={() => setConfirmKind("deactivate")}
              >
                {busy === "deactivate" ? "…" : "Деактивировать"}
              </Button>
            ) : (
              <Button
                variant="secondary"
                disabled={busy !== null}
                onClick={() =>
                  action("activate", () =>
                    usersApi.patch(user.id, { is_active: true }),
                  )
                }
              >
                {busy === "activate" ? "…" : "Активировать"}
              </Button>
            )}
          </div>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
            <li>
              <b>Отозвать все сессии</b> — разлогинит сотрудника на всех
              устройствах. Войти снова можно — через одноразовый код на email.
            </li>
            {user.is_active ? (
              <li>
                <b>Деактивировать</b> — запретит вход в админку. Уже выпущенные
                ссылки на документы продолжат работать; сотрудника нельзя
                удалить, чтобы сохранить историю действий.
              </li>
            ) : (
              <li>
                <b>Активировать</b> — вернёт доступ в админку без сброса прав
                и привязок.
              </li>
            )}
          </ul>
        </CardContent>
      </Card>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <ConfirmDialog
        open={confirmKind === "revoke"}
        title={`Отозвать все активные сессии ${user.full_name}?`}
        description="Сотрудник будет разлогинен на всех устройствах. Для входа потребуется заново получить код."
        confirmLabel="Отозвать сессии"
        busy={busy === "revoke"}
        onConfirm={async () => {
          setConfirmKind(null);
          await action("revoke", () => usersApi.revokeSessions(user.id));
        }}
        onCancel={() => setConfirmKind(null)}
      />

      <ConfirmDialog
        open={confirmKind === "deactivate"}
        title={`Деактивировать ${user.full_name}?`}
        description="Сотрудник не сможет войти в систему до повторной активации. Уже выпущенные ссылки на документы продолжат работать."
        confirmLabel="Деактивировать"
        busy={busy === "deactivate"}
        onConfirm={async () => {
          setConfirmKind(null);
          await action("deactivate", () => usersApi.deactivate(user.id));
        }}
        onCancel={() => setConfirmKind(null)}
      />
    </div>
  );
}

function BackToUsers() {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => navigate("/users")}
      data-testid="back-to-users"
    >
      <span aria-hidden="true" className="mr-1">←</span>К сотрудникам
    </Button>
  );
}

interface ProfileCardProps {
  user: User;
  busy: boolean;
  onSave: (payload: UserPatchPayload) => Promise<void>;
}

function ProfileCard({ user, busy, onSave }: ProfileCardProps) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    full_name: user.full_name,
    role: user.role,
    bitrix_user_id: user.bitrix_user_id ?? "",
  });

  // При новой загрузке пользователя (после save или внешнего апдейта)
  // синхронизируем форму с актуальными значениями, чтобы Отмена
  // возвращала к последнему сохранённому состоянию.
  useEffect(() => {
    setForm({
      full_name: user.full_name,
      role: user.role,
      bitrix_user_id: user.bitrix_user_id ?? "",
    });
  }, [user]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await onSave({
      full_name: form.full_name.trim(),
      role: form.role,
      bitrix_user_id: form.bitrix_user_id.trim() || null,
    });
    setEditing(false);
  }

  function cancel() {
    setForm({
      full_name: user.full_name,
      role: user.role,
      bitrix_user_id: user.bitrix_user_id ?? "",
    });
    setEditing(false);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Профиль</CardTitle>
        {!editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            data-testid="profile-edit"
          >
            Редактировать
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">ФИО</Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={user.email} disabled />
              <p className="text-xs text-muted-foreground">
                Email используется для входа и не редактируется. Чтобы сменить —
                деактивируйте сотрудника и создайте нового.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Роль</Label>
              <Select
                id="role"
                value={form.role}
                onChange={(e) =>
                  setForm({ ...form, role: e.target.value as "admin" | "manager" })
                }
              >
                <option value="manager">Менеджер</option>
                <option value="admin">Админ</option>
              </Select>
              <p className="text-xs text-muted-foreground">
                Менеджер — создаёт свои документы и рассылки, видит только их.
                Админ — то же плюс управление сотрудниками, настройки Bitrix24,
                аудит и доступ ко всем рассылкам.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bitrix_user_id">Bitrix user_id</Label>
              <Input
                id="bitrix_user_id"
                value={form.bitrix_user_id}
                onChange={(e) =>
                  setForm({ ...form, bitrix_user_id: e.target.value })
                }
                placeholder="например, 42"
              />
              <p className="text-xs text-muted-foreground">
                ID пользователя в Bitrix24 (целое число). Без него уведомления
                о просмотре документа и истечении срока ссылки в Bitrix24 не
                приходят.
              </p>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? "Сохранение…" : "Сохранить"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={cancel}
                disabled={busy}
              >
                Отменить
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">ФИО</dt>
            <dd>{user.full_name}</dd>
            <dt className="text-muted-foreground">Email</dt>
            <dd>{user.email}</dd>
            <dt className="text-muted-foreground">Роль</dt>
            <dd>{user.role === "admin" ? "Админ" : "Менеджер"}</dd>
            <dt className="text-muted-foreground">Bitrix user_id</dt>
            <dd>{user.bitrix_user_id ?? "—"}</dd>
            <dt className="text-muted-foreground">Статус</dt>
            <dd>
              {user.is_active ? (
                <span className="inline-block rounded px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700">
                  Активен
                </span>
              ) : (
                <span className="inline-block rounded px-2 py-0.5 text-xs bg-muted text-muted-foreground">
                  Отключён
                </span>
              )}
            </dd>
            <dt className="text-muted-foreground">Последний вход</dt>
            <dd>
              {user.last_login_at
                ? new Date(user.last_login_at).toLocaleString("ru-RU")
                : "—"}
            </dd>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err, { entityLabel: "Сотрудник" });
}
